import Icon from '@/components/icons/Icon';
import type { ReactHookFormRegister } from '@/components/modules/checkout/CheckoutFormFields/CheckoutFormFields';
import { classnames } from '@/helpers';
import { LegacyRef, TextareaHTMLAttributes, forwardRef, useId } from 'react';
import { twMerge } from 'tailwind-merge';

type GenericListInputProps = {
  error?: string;
  label?: string;
  leftIcon?: React.ReactNode;
  info?: string;
  labelClassName?: string;
  leftPadding?: boolean;
  rightPadding?: boolean;
  floatStyle?: boolean;
  rows?: number;
};

type ListInputTextAreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> &
  GenericListInputProps &
  ReactHookFormRegister<any>;

const ListInputTextArea = forwardRef(
  (
    {
      error,
      label,
      leftIcon,
      info,
      labelClassName,
      disabled,
      leftPadding = true,
      rightPadding = true,
      floatStyle = false,
      ...props
    }: ListInputTextAreaProps,
    ref: LegacyRef<HTMLTextAreaElement>,
  ) => {
    // Minted rather than accepted as a prop: callers reuse field ids across surfaces, and
    // `htmlFor` binds to whichever element carries that id first in tree order.
    const inputId = useId();
    if (floatStyle) {
      return (
        <div
          className={classnames(
            leftPadding && 'pl-lg',
            rightPadding && 'pr-lg',
            'flex w-full flex-col',
            disabled && 'opacity-40',
          )}>
          <div
            className={classnames(
              'px-md gap-xs py-xs relative flex items-center rounded-sm border',
              error ? 'border-border_negative' : 'border-border_regular',
              'float-group-textarea',
            )}>
            {leftIcon && <div className="w-7">{leftIcon}</div>}
            <div className="relative w-full">
              {label && (
                <label
                  htmlFor={inputId}
                  className={classnames(
                    'text-fg_secondary text-l absolute left-0 top-2 block',
                    disabled && 'text-fg_disabled',
                    labelClassName,
                  )}>
                  {label}
                </label>
              )}
              <textarea
                id={inputId}
                ref={ref}
                className={
                  'text-fg_primary text-l mt-xs peer w-full placeholder-transparent outline-none disabled:bg-transparent'
                }
                disabled={disabled}
                placeholder={label}
                {...props}
              />
            </div>
          </div>
          {(error || info) && (
            <div className="mt-xxs flex">
              {error && <div className="text-fg_negative text-s">{error}</div>}
              {info && <div className="text-xxs text-black-600 flex flex-1 justify-end">{info}</div>}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={classnames('flex w-full flex-col', disabled && 'opacity-40', leftPadding && 'pl-lg')}>
        {label && (
          <label htmlFor={inputId} className={twMerge('text-black-900 pt-md text-m', labelClassName)}>
            {label}
          </label>
        )}
        <div className={`py-md pr-lg relative flex border-b ${error ? 'border-danger-500' : 'border-black-100'}`}>
          {leftIcon && <div className="pr-md pt-md justify-end">{leftIcon}</div>}
          <textarea
            id={inputId}
            ref={ref}
            className={`placeholder:text-black-600 text-black-900 w-full resize-none text-sm outline-none disabled:bg-transparent`}
            disabled={disabled}
            {...props}
          />
          {error && (
            <div className="pt-md mt-md justify-end">
              <Icon variant="warning" color="danger-500" />
            </div>
          )}
        </div>
        {(error || info) && (
          <div className="mt-xxs flex">
            {error && <div className="text-danger text-xxs">{error}</div>}
            {info && <div className="text-xxs text-black-600 flex flex-1 justify-end">{info}</div>}
          </div>
        )}
      </div>
    );
  },
);

export default ListInputTextArea;
